import { format, formatDistance, parseISO } from 'date-fns';

export const formatCurrency = (amount: number, currency: string = 'INR'): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-IN').format(num);
};

export const formatPercentage = (num: number | string, decimals: number = 2): string => {
  const numValue = typeof num === 'string' ? parseFloat(num) : num;
  if (isNaN(numValue)) return '0.00%';
  return `${numValue.toFixed(decimals)}%`;
};

export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (isNaN(dateObj.getTime())) return 'N/A';
    return format(dateObj, 'MMM dd, yyyy');
  } catch (error) {
    return 'N/A';
  }
};

export const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (isNaN(dateObj.getTime())) return 'N/A';
    return format(dateObj, 'MMM dd, yyyy HH:mm');
  } catch (error) {
    return 'N/A';
  }
};

export const formatRelativeTime = (date: string | Date | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (isNaN(dateObj.getTime())) return 'N/A';
    return formatDistance(dateObj, new Date(), { addSuffix: true });
  } catch (error) {
    return 'N/A';
  }
};

export const getRecommendationColor = (recommendation: string): string => {
  switch (recommendation.toUpperCase()) {
    case 'STRONG_BUY':
    case 'BUY':
      return 'text-green-600 bg-green-50';
    case 'STRONG_SELL':
    case 'SELL':
      return 'text-red-600 bg-red-50';
    case 'HOLD':
      return 'text-yellow-600 bg-yellow-50';
    default:
      return 'text-gray-600 bg-gray-50';
  }
};

export const getRecommendationBadgeClass = (recommendation: string | undefined | null): string => {
  if (!recommendation) {
    return 'bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium';
  }

  const rec = recommendation.toString().toLowerCase().trim();

  switch (rec) {
    case 'strong_buy':
    case 'buy':
      return 'badge-buy';
    case 'strong_sell':
    case 'sell':
      return 'badge-sell';
    case 'hold':
    case 'neutral':
    case 'accumulate':
    case 'add':
      return 'badge-hold';
    default:
      console.log('Unknown recommendation type:', recommendation, 'normalized:', rec);
      return 'bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium';
  }
};

export const getUpsideColor = (upside: number): string => {
  if (upside > 20) return 'text-green-600';
  if (upside > 10) return 'text-green-500';
  if (upside > 0) return 'text-green-400';
  if (upside > -10) return 'text-yellow-500';
  return 'text-red-500';
};

export const getStockScoreBadgeClass = (score: number | string | null | undefined): string => {
  if (score === null || score === undefined || score === '') {
    return 'bg-gray-100 text-gray-600 px-2 py-1 rounded-md text-xs font-medium';
  }
  
  // Convert string to number if needed
  const numScore = typeof score === 'string' ? parseFloat(score) : score;
  
  if (isNaN(numScore)) {
    return 'bg-gray-100 text-gray-600 px-2 py-1 rounded-md text-xs font-medium';
  }
  
  if (numScore >= 10.0) {
    return 'bg-blue-500 text-emerald-700 px-2 py-1 rounded-md text-xs font-bold border border-emerald-200';
  } else if (numScore >= 9.0) {
    return 'bg-green-500 text-emerald-700 px-2 py-1 rounded-md text-xs font-bold border border-emerald-200';
  } else if (numScore >= 8.0) {
    return 'bg-green-100 text-green-700 px-2 py-1 rounded-md text-xs font-bold border border-green-200';
  } else if (numScore >= 7.0) {
    return 'bg-lime-100 text-lime-700 px-2 py-1 rounded-md text-xs font-bold border border-lime-200';
  } else if (numScore >= 6.0) {
    return 'bg-yellow-100 text-yellow-700 px-2 py-1 rounded-md text-xs font-bold border border-yellow-200';
  } else if (numScore >= 5.0) {
    return 'bg-orange-100 text-orange-700 px-2 py-1 rounded-md text-xs font-bold border border-orange-200';
  } else {
    return 'bg-red-100 text-red-700 px-2 py-1 rounded-md text-xs font-bold border border-red-200';
  }
};

export const getStockScoreColor = (score: number | string | null | undefined): string => {
  if (score === null || score === undefined || score === '') {
    return 'text-gray-500';
  }
  
  // Convert string to number if needed
  const numScore = typeof score === 'string' ? parseFloat(score) : score;
  
  if (isNaN(numScore)) {
    return 'text-gray-500';
  }
  
  if (numScore >= 9.0) return 'text-emerald-600';
  if (numScore >= 8.0) return 'text-green-600';
  if (numScore >= 7.0) return 'text-lime-600';
  if (numScore >= 6.0) return 'text-yellow-600';
  if (numScore >= 5.0) return 'text-orange-600';
  return 'text-red-600';
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

export const classNames = (...classes: (string | undefined | null | boolean)[]): string => {
  return classes.filter(Boolean).join(' ');
};